import React, { Fragment, Component } from 'react';
import {
    Grid,
    CircularProgress,
    Tooltip,
    Dialog,
    DialogContent,
    DialogActions,
    Button,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { withTranslation } from 'react-i18next';
import { Flight, Launch } from '@mui/icons-material';
import SvgIcon from '@mui/material/SvgIcon';
import GetAppIcon from '@mui/icons-material/GetApp';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import moment from 'moment';
import { ReactComponent as AmericanIcon } from '/public/travelicons/flighticons/American.svg';
import { ReactComponent as DeltaIcon } from '/public/travelicons/flighticons/Delta.svg';
import { ReactComponent as FrontierIcon } from '/public/travelicons/flighticons/Frontier.svg';
import { ReactComponent as JetBlueIcon } from '/public/travelicons/flighticons/JetBlue.svg';
import { ReactComponent as SouthwestIcon } from '/public/travelicons/flighticons/Southwest.svg';
import { ReactComponent as SpiritIcon } from '/public/travelicons/flighticons/Spirit.svg';
import { ReactComponent as UnitedIcon } from '/public/travelicons/flighticons/United.svg';
import { ReactComponent as GenericAirIcon } from '/public/travelicons/flighticons/GenericAir.svg';
import { COLORS } from '../../../../utils/Application_Constants';
import { getTravelItinerary } from '../../../../services/AssignmentsService';

const styles = (theme) => ({
    AccordionSummaryRoot: {
        padding: '8px 20px',
    },
    title: {
        fontWeight: 'bold',
        fontSize: '16px',
        marginBottom: '4px',
        color: COLORS.RAVEN,
    },
    value: {
        fontSize: '14px',
        color: COLORS.RAVEN,
    },
    clickableLink: {
        fontWeight: '500',
        color: COLORS.TRUE_BLUE,
        fontSize: '14px',
        textDecoration: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
    },
    topRightIcon: {
        position: 'absolute',
        top: '8px',
        right: '16px',
    },
    flightCard: {
        marginTop: '5px',
        backgroundColor: 'white',
        padding: '16px',
        minWidth: '100%',
        borderRadius: '8px',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
        position: 'relative',
    },
    iconClass: {
        verticalAlign: 'middle',
        color: COLORS.TRUE_BLUE,
        fontSize: '36px',
    },
    buttonContainer: {
        display: 'flex',
        gap: '24px',
        marginTop: '8px',
        marginLeft: '16px',
    },
    buttonIcon: {
        marginRight: '8px',
        fontSize: '16px',
        color: COLORS.TRUE_BLUE,
        backgroundColor: COLORS.LIGHT_GRAY,
        borderRadius: '50%',
        padding: '4px',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
        verticalAlign: 'middle',
    },
    button: {
        background: 'none',
        border: 'none',
        fontWeight: '500',
        color: COLORS.TRUE_BLUE,
        fontSize: '14px',
        textDecoration: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
    },
    disabledButton: {
        color: 'gray',
        fontWeight: '500',
        fontSize: '14px',
        cursor: 'not-allowed',
        textDecoration: 'none',
        pointerEvents: 'none',
    },
    section: {
        marginBottom: '12px',
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    dialogPaperRoot: {
        padding: '40px',
        gap: '16px',
    },
    dialogContentRoot: {
        padding: '0',
        '&:first-child': {
            paddingTop: '0',
        },
    },
    dialogActionsRoot: {
        padding: '0',
    },
});

class FlightsV3 extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            cancelModalOpen: false,
        };
    }

    renderIcon = (vendorName) => {
        const { classes } = this.props;
        switch (vendorName) {
            case 'American Airlines':
                return (
                    <SvgIcon
                        aria-labelledby="americanTitleID"
                        component={AmericanIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
            case 'Delta Air Lines':
                return (
                    <SvgIcon
                        aria-labelledby="deltaTitleID"
                        component={DeltaIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
            case 'Frontier Airlines':
                return (
                    <SvgIcon
                        aria-labelledby="frontierTitleID"
                        component={FrontierIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
            case 'Jetblue':
                return (
                    <SvgIcon
                        aria-labelledby="jetblueTitleID"
                        component={JetBlueIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
            case 'Southwest Airlines':
                return (
                    <SvgIcon
                        aria-labelledby="southwestTitleID"
                        component={SouthwestIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
            case 'Spirit Airlines':
                return (
                    <SvgIcon
                        aria-labelledby="spiritTitleID"
                        component={SpiritIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
            case 'United Airlines':
                return (
                    <SvgIcon
                        aria-labelledby="unitedTitleID"
                        component={UnitedIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
            default:
                return (
                    <SvgIcon
                        aria-labelledby="genericAirTitleID"
                        component={GenericAirIcon}
                        className={classes.iconClass}
                        viewBox="0 0 38 38"
                    />
                );
        }
    };

    renderErrorDialog() {
        const { cancelModalOpen } = this.state;
        const { classes } = this.props;

        return (
            <Dialog
                open={cancelModalOpen}
                onClose={() => this.setState({ cancelModalOpen: false })}
                aria-labelledby="error-modal-title"
                aria-describedby="error-modal-description"
                classes={{ paper: classes.dialogPaperRoot }}
                fullWidth
                maxWidth="xs"
            >
                <DialogContent
                    id="error-modal-description"
                    classes={{ root: classes.dialogContentRoot }}
                >
                    The travel document is not currently available. For immediate assistance, please
                    contact your Clinician Relations Specialist.
                </DialogContent>
                <DialogActions classes={{ root: classes.dialogActionsRoot }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => this.setState({ cancelModalOpen: false })}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    render() {
        const { classes, item, externalId, assignmentId } = this.props;
        const { isLoading } = this.state;
        const isItineraryAvailable = !!item.eItinLink;
        const isCompletedOver30Days =
            item.completedDate &&
            moment(item.completedDate).isBefore(moment().subtract(30, 'days'));

        return (
            <Fragment key={`${item.tripKey}-${item.flightNumber}`}>
                {this.renderErrorDialog()}
                <Grid container direction="column" className={classes.flightCard}>
                    <div className={classes.row}>
                        <div className={classes.column}>
                            <div className={classes.section}>
                                <div className={classes.title}>FLIGHT</div>
                                <div
                                    className={classes.value}
                                >{`${item.originCityDesc} - ${item.destCityDesc}`}</div>
                            </div>
                        </div>
                        <div className={classes.topRightIcon}>
                            {this.renderIcon(item.vendorName)}
                        </div>
                    </div>
                    <div className={classes.row}>
                        <div className={classes.column}>
                            <div className={classes.section}>
                                <div className={classes.title}>AIRLINE</div>
                                <div className={classes.value}>{item.vendorName}</div>
                            </div>
                        </div>
                        <div className={classes.column}>
                            <div className={classes.section}>
                                <div className={classes.title}>FLIGHT #</div>
                                <div className={classes.value}>{item.flightNumber}</div>
                            </div>
                        </div>
                    </div>
                    <div className={classes.row}>
                        <div className={classes.column}>
                            <div className={classes.section}>
                                <div className={classes.title}>DEPARTURE</div>
                                <div className={classes.value}>{`${moment(
                                    item.originatedDate
                                ).format('MM/DD/YYYY h:mm a')}`}</div>
                            </div>
                        </div>
                        <div className={classes.column}>
                            <div className={classes.section}>
                                <div className={classes.title}>ARRIVAL</div>
                                <div className={classes.value}>{`${moment(
                                    item.completedDate
                                ).format('MM/DD/YYYY h:mm a')}`}</div>
                            </div>
                        </div>
                        <div className={classes.column}>
                            <div className={classes.section}>
                                <div className={classes.title}>CONFIRMATION</div>
                                <div className={classes.value}>
                                    <InsertDriveFileOutlinedIcon fontSize="small" />
                                    {item.confirmationNumber}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={classes.buttonContainer}>
                        {(() => {
                            if (!isCompletedOver30Days && isItineraryAvailable) {
                                return (
                                    <>
                                        <a
                                            href={item.eItinLink}
                                            className={classes.clickableLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <Launch className={classes.buttonIcon} />
                                            Itinerary
                                        </a>
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                this.setState({ isLoading: true });
                                                try {
                                                    await getTravelItinerary(
                                                        externalId,
                                                        assignmentId,
                                                        item.recordLocator,
                                                        moment(item.firstTravelDate).format(
                                                            'MM-DD-YYYY'
                                                        ),
                                                        moment(item.lastTravelDate).format(
                                                            'MM-DD-YYYY'
                                                        )
                                                    );
                                                } catch (error) {
                                                    console.error('Error downloading file:', error);
                                                    if (error?.response?.status === 404) {
                                                        this.setState({ cancelModalOpen: true });
                                                    } else {
                                                        alert(
                                                            'Failed to download the file. Please try again.'
                                                        );
                                                    }
                                                } finally {
                                                    this.setState({ isLoading: false });
                                                }
                                            }}
                                            className={`${classes.button} ${
                                                isLoading ? classes.disabledButton : ''
                                            }`}
                                            disabled={isLoading}
                                            aria-label="Download Itinerary"
                                        >
                                            {isLoading ? (
                                                <CircularProgress
                                                    size={20}
                                                    className={classes.buttonIcon}
                                                />
                                            ) : (
                                                <GetAppIcon className={classes.buttonIcon} />
                                            )}
                                            Download
                                        </button>
                                    </>
                                );
                            }

                            if (!isCompletedOver30Days) {
                                return (
                                    <>
                                        <Tooltip title="Booking information not available">
                                            <span>
                                                <button
                                                    type="button"
                                                    onClick={(e) => e.preventDefault()}
                                                    className={`${classes.clickableLink} ${classes.disabledButton}`}
                                                    disabled
                                                    aria-label="Itinerary unavailable"
                                                >
                                                    <Launch
                                                        className={`${classes.buttonIcon} ${classes.disabledButton}`}
                                                    />
                                                    Itinerary
                                                </button>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Travel information not available">
                                            <span>
                                                <button
                                                    type="button"
                                                    className={`${classes.button} ${classes.disabledButton}`}
                                                    disabled
                                                    aria-label="Download Itinerary"
                                                >
                                                    <GetAppIcon
                                                        className={`${classes.buttonIcon} ${classes.disabledButton}`}
                                                    />
                                                    Download
                                                </button>
                                            </span>
                                        </Tooltip>
                                    </>
                                );
                            }

                            return null;
                        })()}
                    </div>
                </Grid>
            </Fragment>
        );
    }
}

export default withTranslation()(withStyles(styles)(FlightsV3));
